import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import { useRole } from "../../context/RoleContext";
import Button from "../common/Button";
import { TOUR_STEPS } from "./tourSteps";

const START_EVENT = "pms:start-tour";
const seenKey = (employeeId, view) => `pms_tour_seen_${employeeId || "user"}_${view}`;

export const startProductTour = () => window.dispatchEvent(new Event(START_EVENT));

function readSeen(key) {
  try { return localStorage.getItem(key) === "1"; } catch { return true; }
}
function writeSeen(key) {
  try { localStorage.setItem(key, "1"); } catch {  }
}

function findTarget(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 ? el : null;
}

const CARD_W = 340;
const GAP = 14;
const PAD = 6;

function placeCard(rect, cardH) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const clampX = (x) => Math.max(12, Math.min(x, vw - CARD_W - 12));
  const clampY = (y) => Math.max(12, Math.min(y, vh - cardH - 12));
  if (!rect) return { left: (vw - CARD_W) / 2, top: Math.max(12, (vh - cardH) / 2) };
  if (rect.right + GAP + CARD_W <= vw - 12) return { left: rect.right + GAP, top: clampY(rect.top + rect.height / 2 - cardH / 2) };
  if (rect.bottom + GAP + cardH <= vh - 12) return { left: clampX(rect.left + rect.width / 2 - CARD_W / 2), top: rect.bottom + GAP };
  if (rect.top - GAP - cardH >= 12) return { left: clampX(rect.left + rect.width / 2 - CARD_W / 2), top: rect.top - GAP - cardH };
  return { left: clampX(rect.left - GAP - CARD_W), top: clampY(rect.top) };
}

export default function ProductTour() {
  const { profile, activeView } = useRole();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardH, setCardH] = useState(200);
  const cardRef = useRef(null);

  const employeeId = profile?.employeeId || profile?.id;
  const firstName = String(profile?.name || "").split(" ")[0] || "there";
  const steps = useMemo(() => TOUR_STEPS[activeView] || TOUR_STEPS.employee, [activeView]);
  const key = seenKey(employeeId, activeView);

  useEffect(() => {
    if (!profile || !activeView || readSeen(key)) return undefined;
    const timer = setTimeout(() => { setIndex(0); setOpen(true); }, 900);
    return () => clearTimeout(timer);
  }, [profile, activeView, key]);

  useEffect(() => {
    const start = () => { setIndex(0); setOpen(true); };
    window.addEventListener(START_EVENT, start);
    return () => window.removeEventListener(START_EVENT, start);
  }, []);

  const close = useCallback(() => { writeSeen(key); setOpen(false); }, [key]);

  const go = useCallback((from, dir) => {
    let i = from;
    while (i >= 0 && i < steps.length) {
      if (!steps[i].target || findTarget(steps[i].target)) { setIndex(i); return; }
      i += dir;
    }
    if (dir > 0) close(); else setIndex(0);
  }, [steps, close]);

  const step = open ? steps[index] : null;

  useLayoutEffect(() => {
    if (!step) return undefined;
    const el = findTarget(step.target);
    if (step.target && !el) { go(index + 1, 1); return undefined; }
    if (el) el.scrollIntoView({ block: "nearest", inline: "nearest" });
    const measure = () => {
      const r = el ? el.getBoundingClientRect() : null;
      setRect(r ? { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2, right: r.right + PAD, bottom: r.bottom + PAD } : null);
      if (cardRef.current) setCardH(cardRef.current.offsetHeight);
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [step, index, go]);

  useEffect(() => { if (open) cardRef.current?.querySelector(".tour-actions .btn-primary")?.focus(); }, [open, index]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); close(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); go(index + 1, 1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); go(index - 1, -1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, go, close]);

  if (!step) return null;

  const isLast = index === steps.length - 1;
  const pos = placeCard(rect, cardH);
  const title = step.title.replace("{name}", firstName);

  return (
    <div className="tour-root" role="dialog" aria-modal="true" aria-labelledby="tour-title" aria-describedby="tour-body">
      <div className={`tour-backdrop ${rect ? "" : "is-dim"}`} />
      {rect && <div className="tour-spot" style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }} aria-hidden="true" />}
      <div className="tour-card" ref={cardRef} style={{ top: pos.top, left: pos.left, width: CARD_W }}>
        <button type="button" className="tour-close" onClick={close} aria-label="Close the tour"><FiX size={16} /></button>
        <p className="tour-count">Step {index + 1} of {steps.length}</p>
        <h3 id="tour-title" className="tour-title">{title}</h3>
        <p id="tour-body" className="tour-body">{step.body}</p>
        <div className="tour-dots" aria-hidden="true">
          {steps.map((s, i) => <span key={s.title + i} className={i === index ? "is-active" : i < index ? "is-done" : ""} />)}
        </div>
        <div className="tour-actions">
          {index === 0
            ? <Button variant="ghost" size="sm" onClick={close}>Skip tour</Button>
            : <Button variant="ghost" size="sm" onClick={() => go(index - 1, -1)}>Back</Button>}
          <Button size="sm" onClick={() => (isLast ? close() : go(index + 1, 1))}>
            {isLast ? "Finish" : index === 0 ? "Start the tour" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
}
